/**
 * Device-side PKCS#10 helper for the reader webapp / future Capacitor shell.
 *
 * Private keys are Web Crypto CryptoKey with extractable:false — they cannot
 * be exported or POSTed. Native apps should instead generate in Secure Enclave
 * / Android Keystore and send only the CSR PEM to POST /api/v1/certificates/csr.
 */

function concat(...chunks: Uint8Array[]): Uint8Array {
  const len = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(len);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.length;
  }
  return out;
}

function derLength(n: number): Uint8Array {
  if (n < 128) return Uint8Array.of(n);
  if (n < 256) return Uint8Array.of(0x81, n);
  if (n < 65536) return Uint8Array.of(0x82, (n >> 8) & 0xff, n & 0xff);
  throw new Error("DER length too large");
}

function derTlv(tag: number, content: Uint8Array): Uint8Array {
  return concat(Uint8Array.of(tag), derLength(content.length), content);
}

function derSeq(...parts: Uint8Array[]): Uint8Array {
  return derTlv(0x30, concat(...parts));
}

function derSet(...parts: Uint8Array[]): Uint8Array {
  return derTlv(0x31, concat(...parts));
}

function encodeOid(oid: string): Uint8Array {
  const parts = oid.split(".").map((x) => Number(x));
  const out: number[] = [40 * (parts[0] ?? 0) + (parts[1] ?? 0)];
  for (const part of parts.slice(2)) {
    const stack: number[] = [];
    let n = part;
    stack.push(n & 0x7f);
    n >>= 7;
    while (n > 0) {
      stack.push((n & 0x7f) | 0x80);
      n >>= 7;
    }
    for (let i = stack.length - 1; i >= 0; i--) out.push(stack[i] ?? 0);
  }
  return derTlv(0x06, Uint8Array.from(out));
}

function derUtf8(s: string): Uint8Array {
  return derTlv(0x0c, new TextEncoder().encode(s));
}

function derPrintable(s: string): Uint8Array {
  return derTlv(0x13, new TextEncoder().encode(s));
}

function derNull(): Uint8Array {
  return Uint8Array.of(0x05, 0x00);
}

function derBitString(bytes: Uint8Array): Uint8Array {
  return derTlv(0x03, concat(Uint8Array.of(0x00), bytes));
}

function derIntegerZero(): Uint8Array {
  return Uint8Array.of(0x02, 0x01, 0x00);
}

function derContext0Empty(): Uint8Array {
  return Uint8Array.of(0xa0, 0x00);
}

function rdn(oid: string, value: Uint8Array): Uint8Array {
  return derSet(derSeq(encodeOid(oid), value));
}

function toPem(der: Uint8Array, type: string): string {
  let bin = "";
  for (let i = 0; i < der.length; i++) bin += String.fromCharCode(der[i] ?? 0);
  const b64 = btoa(bin);
  const lines = b64.match(/.{1,64}/g) ?? [b64];
  return `-----BEGIN ${type}-----\n${lines.join("\n")}\n-----END ${type}-----\n`;
}

export type DeviceCsrResult = {
  csrPem: string;
  publicKeySpkiPem: string;
  /** Non-extractable private key — never serialize this. */
  keyPair: CryptoKeyPair;
};

export async function generateDeviceCsr(input: {
  commonName: string;
  /** Verified national code (X.520 serialNumber); the CA matches it to the e-sign profile. */
  serialNumber: string;
  country?: string;
}): Promise<DeviceCsrResult> {
  const cn = input.commonName.trim();
  if (cn.length < 2) {
    throw new Error("commonName is required for the CSR subject");
  }
  const serialNumber = input.serialNumber.trim();
  if (!/^\d{10}$/.test(serialNumber)) {
    throw new Error("serialNumber (national code) is required for the CSR subject");
  }
  const keyPair = await crypto.subtle.generateKey(
    {
      name: "RSASSA-PKCS1-v1_5",
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: "SHA-256",
    },
    false,
    ["sign", "verify"],
  );
  if (keyPair.privateKey.extractable) {
    throw new Error("کلید خصوصی نباید قابل استخراج باشد");
  }
  const spki = new Uint8Array(await crypto.subtle.exportKey("spki", keyPair.publicKey));
  const subject = derSeq(
    rdn("2.5.4.6", derPrintable(input.country ?? "IR")),
    rdn("2.5.4.3", derUtf8(cn)),
    rdn("2.5.4.5", derPrintable(serialNumber)),
  );
  const certificationRequestInfo = derSeq(
    derIntegerZero(),
    subject,
    spki,
    derContext0Empty(),
  );
  const signature = new Uint8Array(
    await crypto.subtle.sign(
      "RSASSA-PKCS1-v1_5",
      keyPair.privateKey,
      certificationRequestInfo as BufferSource,
    ),
  );
  const sigAlg = derSeq(encodeOid("1.2.840.113549.1.1.11"), derNull());
  const csrDer = derSeq(certificationRequestInfo, sigAlg, derBitString(signature));
  return {
    csrPem: toPem(csrDer, "CERTIFICATE REQUEST"),
    publicKeySpkiPem: toPem(spki, "PUBLIC KEY"),
    keyPair,
  };
}
